/**
 * U-13 «Выбор интересов».
 *
 * ПС §07, BS §17.13 `GET /dictionaries/interests`: справочник приходит с
 * сервера уже локализованным (`label`) и отображается как есть (CC §13.1,
 * «Серверные тексты»). Предел выбора — `Config.limits.interests_max`, в коде
 * числа нет.
 */
import { useMemo, useState } from 'react'

import type { Interest } from '@shared/api/types'
import { Chip, EmptyState, SearchInput, Sheet } from '@shared/ui'

import styles from './interests.module.css'

/** Порядок разделов: справочник отдаёт `category` строкой без порядка. */
const CATEGORY_TITLE: Record<string, string> = {
  lifestyle: 'Образ жизни',
  food: 'Еда и напитки',
  sport: 'Спорт и движение',
  sports: 'Спорт и движение',
  culture: 'Культура',
  tech: 'Технологии',
  outdoor: 'На воздухе',
  outdoors: 'На воздухе',
  social: 'Общение',
  travel: 'Путешествия',
  other: 'Другое',
}

/** Категория без перевода показывается как «Другое», а не кодом справочника. */
function categoryTitle(category: string): string {
  return CATEGORY_TITLE[category] ?? CATEGORY_TITLE['other']!
}

export function InterestsPicker({
  open,
  interests,
  selected,
  max,
  onToggle,
  onClose,
}: {
  open: boolean
  interests: readonly Interest[]
  selected: readonly string[]
  max: number
  onToggle: (interest: Interest) => void
  onClose: () => void
}): React.ReactElement {
  const [query, setQuery] = useState('')

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const visible = needle ? interests.filter((item) => item.label.toLowerCase().includes(needle)) : interests

    const byCategory = new Map<string, Interest[]>()
    for (const item of visible) {
      const key = item.category ?? 'other'
      const list = byCategory.get(key)
      if (list) list.push(item)
      else byCategory.set(key, [item])
    }

    return [...byCategory.entries()]
  }, [interests, query])

  const full = selected.length >= max

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Интересы"
      description={`Выбрано ${selected.length} из ${max}. По ним мы подбираем, с кем у вас есть общее.`}
    >
      <div className={styles['search']}>
        <SearchInput
          value={query}
          onValueChange={setQuery}
          aria-label="Поиск интереса"
          placeholder="Найти интерес"
        />
      </div>

      {groups.length === 0 ? (
        <EmptyState icon="search" title="Ничего не найдено" text="Попробуйте другое слово." />
      ) : (
        <div className={styles['groups']}>
          {groups.map(([category, items]) => (
            <section key={category}>
              <h3 className={styles['groupTitle']}>{categoryTitle(category)}</h3>
              <div className={styles['chips']}>
                {items.map((interest) => {
                  const active = selected.includes(interest.id)
                  return (
                    <Chip
                      key={interest.id}
                      selected={active}
                      // Предел достигнут — новые отключены, выбранные снимаются.
                      disabled={!active && full}
                      onClick={() => onToggle(interest)}
                    >
                      {interest.label}
                    </Chip>
                  )
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      {full ? (
        <p className={styles['limit']}>Достигнут предел — снимите один, чтобы выбрать другой.</p>
      ) : null}
    </Sheet>
  )
}
